import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authClient } from "../lib/auth.ts";
import { assessPassword, PASSWORD_MIN_LENGTH } from "../lib/password.ts";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const nav = useNavigate();
  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [busy, setBusy]           = useState(false);
  const [err, setErr]             = useState<string | null>(null);
  const [success, setSuccess]     = useState(false);

  // Token is required. We don't try to validate it client-side
  // beyond presence — the server will reject expired/invalid tokens
  // when we POST. This way the page renders fast and we don't make
  // an extra round-trip just to render an error state.
  if (!token) {
    return (
      <div className="center">
        <div className="card compact">
          <h2>Invalid reset link</h2>
          <p className="muted">
            This password-reset link is missing its token. Request a new one
            below.
          </p>
          <p className="muted mt">
            <Link to="/forgot-password">Request a new reset link</Link>
          </p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="center">
        <div className="card compact">
          <h2>Password updated</h2>
          <p className="muted">
            Your password has been changed. You can now sign in.
          </p>
          <p className="muted mt">
            <Link to="/login">Continue to sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="center">
      <div className="card compact">
        <h2>Choose a new password</h2>
        <form
          className="formstack"
          onSubmit={async (e) => {
            e.preventDefault();
            setErr(null);
            if (password !== confirm) {
              setErr("Passwords don't match");
              return;
            }
            setBusy(true);
            const r = await authClient.resetPassword({ newPassword: password, token });
            setBusy(false);
            if (r.error) {
              setErr(r.error.message ?? "Couldn't reset password");
              return;
            }
            setSuccess(true);
            // Bounce to /login after a short pause so the success
            // message is actually visible.
            setTimeout(() => nav("/login"), 2000);
          }}
        >
          <label>
            New password ({PASSWORD_MIN_LENGTH}+ chars, mix 3 of: a-z, A-Z, 0-9, symbols)
            <input
              type="password"
              required
              minLength={PASSWORD_MIN_LENGTH}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {(() => {
              const a = assessPassword(password);
              return a.hint ? <div className="err" style={{ marginTop: "0.25rem" }}>{a.hint}</div> : null;
            })()}
          </label>
          <label>
            Confirm password
            <input
              type="password"
              required
              minLength={PASSWORD_MIN_LENGTH}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </label>
          {err && <div className="err">{err}</div>}
          <button
            type="submit"
            disabled={busy || !assessPassword(password).ok}
          >
            {busy ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}
